import { useForm, FormProvider, Controller } from "react-hook-form";
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Select, MenuItem, FormControl, InputLabel
} from "@mui/material";
import { useEffect } from "react";
import SelectIngredienteAtivoMultiple
    from "../../../../../../../components/SelectsSimpleApiQuery/SelectIngredienteAtivo/Multiple";
import { IDetalharAgrotoxico} from "../../../../../../../types/api/receituarioAgronomico/detalhar-agrotoxico";
import { enqueueSnackbar } from "notistack";
import {useMutation, useQueryClient} from "react-query";
import axiosClient from "../../../../../../../lib/axios";

interface PropsEditarAgrotoxico {
    open: boolean;
    onClose: () => void;
    defaultValue: IDetalharAgrotoxico;
    onSubmit: (values: any) => void;
}

export default function DialogEditarAgrotoxico({ open, onClose, defaultValue }: PropsEditarAgrotoxico) {
    const queryClient = useQueryClient();
    const methods = useForm({
        defaultValues: {
            marca_comercial: defaultValue?.marca_comercial ?? "",
            titular_registro: defaultValue?.titular_registro ?? "",
            ingredientes: defaultValue?.ingrediente_ativo ? defaultValue.ingrediente_ativo : [],
            classe: defaultValue?.classe ?? "",
            modo_acao: defaultValue?.modo_acao ?? "",
            classe_toxicologica: defaultValue?.classe_toxicologica ?? "",
            classe_ambiental: defaultValue?.classe_ambiental ?? "",
            organico: defaultValue?.organico ?? "",
            empresa_pais_tipo: defaultValue?.empresa_pais_tipo ?? "",
        }
    });
    useEffect(() => {
        methods.reset({
            marca_comercial: defaultValue?.marca_comercial ?? "",
            titular_registro: defaultValue?.titular_registro ?? "",
            ingredientes: defaultValue?.ingrediente_ativo ? defaultValue.ingrediente_ativo : [],
            classe: defaultValue?.classe ?? "",
            modo_acao: defaultValue?.modo_acao ?? "",
            classe_toxicologica: defaultValue?.classe_toxicologica ?? "",
            classe_ambiental: defaultValue?.classe_ambiental ?? "",
            organico: defaultValue?.organico ?? "",
            empresa_pais_tipo: defaultValue?.empresa_pais_tipo ?? "",
        });
    }, [defaultValue, methods]);

    const {mutate: editarAgrotoxico} = useMutation({
        mutationKey: "editar-agrotoxico",
        mutationFn: (data: IDetalharAgrotoxico) =>
            axiosClient.put(`/receituario-agronomico/agrotoxicos/editar-agrotoxico/${defaultValue.id}`, data),
        onError: () => {
            enqueueSnackbar("Erro ao editar agrotóxico!", {variant: "error"});
        },
        onSuccess: () => {
            queryClient.invalidateQueries(`detalhar-agrotoxico/${defaultValue.id}`);
            enqueueSnackbar("Agrotóxico editado com sucesso!", {
                variant: "success",
            });
        },
    });

    const handleFormSubmit = (data: any) => {
        onClose()
        const payload = {
            ...data,
            ingrediente_ativo_id: data.ingredientes.id,
        }
        editarAgrotoxico(payload);
    };

    return (
        <Dialog open={open} fullWidth maxWidth="md">
            <DialogTitle>Editar Agrotóxico</DialogTitle>
            <DialogContent>
                <FormProvider {...methods}>
                        <Grid container spacing={2} mt={1}>
                            <Grid size={{xs: 12, md: 6}}>
                                <Controller
                                    name="marca_comercial"
                                    control={methods.control}
                                    rules={{ required: "Campo obrigatório" }}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            label="Marca Comercial"
                                            fullWidth
                                            required
                                            error={fieldState.invalid}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{xs: 12, md: 6}}>
                                <Controller
                                    name="titular_registro"
                                    control={methods.control}
                                    rules={{ required: "Campo obrigatório" }}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            label="Titular do Registro"
                                            fullWidth
                                            required
                                            error={fieldState.invalid}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{xs: 12, md: 6}}>
                                <Controller
                                    name="ingredientes"
                                    control={methods.control}
                                    rules={{ required: "Campo obrigatório" }}
                                    render={({ field, fieldState: {invalid} })=> (
                                        <SelectIngredienteAtivoMultiple
                                            required
                                            error={invalid}
                                            unique_select={true}
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{xs: 12, md: 6}}>
                                <Controller
                                    name="classe"
                                    control={methods.control}
                                    rules={{ required: "Campo obrigatório" }}
                                    render={({ field, fieldState: { invalid, error } }) => (
                                        <TextField
                                            {...field}
                                            label="Classe"
                                            fullWidth
                                            required
                                            error={invalid}
                                            helperText={error?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{xs: 12, md: 6}}>
                                <Controller
                                    name="modo_acao"
                                    control={methods.control}
                                    rules={{ required: "Campo obrigatório" }}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            required
                                            label="Modo de Ação"
                                            fullWidth
                                            error={fieldState.invalid}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{xs: 12, md: 6}}>
                                <Controller
                                    name="classe_toxicologica"
                                    control={methods.control}
                                    rules={{ required: "Campo obrigatório" }}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            label="Classe Toxicológica"
                                            fullWidth
                                            required
                                            error={fieldState.invalid}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{xs: 12, md: 6}}>
                                <Controller
                                    name="classe_ambiental"
                                    control={methods.control}
                                    rules={{ required: "Campo obrigatório" }}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            label="Classificação Ambiental"
                                            fullWidth
                                            required
                                            error={fieldState.invalid}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{xs: 12, md: 6}}>
                                <Controller
                                    name="organico"
                                    control={methods.control}
                                    rules={{ required: "Campo obrigatório" }}
                                    render={({ field, fieldState }) => (
                                        <FormControl fullWidth required error={fieldState.invalid}>
                                            <InputLabel id="organico-label">Orgânico</InputLabel>
                                            <Select
                                                {...field}
                                                labelId="organico-label"
                                                label="Orgânico"
                                                value={field.value || ""}
                                                onChange={field.onChange}
                                            >
                                                <MenuItem value="SIM">SIM</MenuItem>
                                                <MenuItem value="NAO">NAO</MenuItem>
                                                <MenuItem value="OUTROS">OUTROS</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                />
                            </Grid>
                            <Grid size={{xs: 12}}>
                                <Controller
                                    name="empresa_pais_tipo"
                                    control={methods.control}
                                    rules={{ required: "Campo obrigatório" }}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            label="Empresa/País/Tipo"
                                            fullWidth
                                            required
                                            error={fieldState.invalid}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                </FormProvider>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>
                    Cancelar
                </Button>
                <Button
                    onClick={methods.handleSubmit(handleFormSubmit)}
                    variant="contained"
                >
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    );
}